import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAlert } from 'react-alert';
import { useSelector } from 'react-redux';
import { Modal, Form, InputGroup, Table } from 'react-bootstrap';
import MetaData from '../../layouts/MetaData';
import Loader from '../../layouts/Loader';
import RoleShell from '../../components/Enrollment/RoleShell';
import { EcPendingCard } from '../../components/DeviceRequests/DeviceRequestBanners';
import { formatDob } from '../../utils/dateFormat';
import PatientBasicsForm from '../../components/Enrollment/PatientBasicsForm';
import { searchOnEnter, searchOrReset } from '../../components/searchOnEnter';
import { Prod01 } from '../../constants/ActionType';
import { useSortableTable, SortableTh, serverSortParams } from '../../components/SortableTable';

const ENROLLMENT_PATIENT_SORT_COLUMNS = { name: {}, emrId: {}, dob: {}, program: {}, insurance: {} };

const LIMIT = 25;

const EnrollmentPatients = () => {
    const alert = useAlert();
    const role = useSelector((s) => s.auth?.user?.role);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ patients: [], count: 0 });
    const [loading, setLoading] = useState(false);
    const tableSort = useSortableTable(data.patients, { id: 'enrollment.patients', columns: ENROLLMENT_PATIENT_SORT_COLUMNS, manual: true, defaultSort: { key: 'name', dir: 'asc' } });
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${Prod01}/enrollment/patients`, { params: { search: query, page, limit: LIMIT, ...(serverSortParams(tableSort, Object.keys(ENROLLMENT_PATIENT_SORT_COLUMNS)) || {}) } });
            setData(res.data);
        } catch (err) {
            alert.error(err?.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [query, page, tableSort.sortKey, tableSort.sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        load();
    }, [load]);

    const runSearch = (v) => {
        setPage(1);
        setQuery(String(v || '').trim());
    };
    const reset = () => {
        setSearch('');
        runSearch('');
    };
    const pages = Math.max(1, Math.ceil((data.count || 0) / LIMIT));

    return (
        <>
            <MetaData title="Patients" />
            <RoleShell title="Patients">
                {['enrollment', 'admin'].includes(role) && <EcPendingCard />}
                <div className="row-display">
                    <h5 style={{ color: '#004aad' }}>
                        <i className="fas fa-user-injured"></i> <strong>&nbsp;Patients</strong>
                    </h5>
                    <button className="add-staff-btn" type="button" onClick={() => setEditing({})}>
                        <small>Add Patient</small>
                    </button>
                </div>
                <hr />
                <InputGroup className="mb-3" style={{ maxWidth: 520 }}>
                    <Form.Control
                        placeholder="Search by first name, last name or EHR-ID ..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={searchOnEnter(runSearch, reset)}
                    />
                    <button className="btn btn-outline-primary btn-sm" type="button" onClick={() => searchOrReset(search, runSearch, reset)}>
                        Search
                    </button>
                </InputGroup>
                {loading ? <Loader /> : (
                    <Table className="table table-sm table-striped">
                        <thead>
                            <tr>
                                <SortableTh sortKey="name" sort={tableSort}>Name</SortableTh><SortableTh sortKey="emrId" sort={tableSort}>EHR-ID</SortableTh>
                                <SortableTh sortKey="dob" sort={tableSort}>DOB</SortableTh><SortableTh sortKey="program" sort={tableSort}>Program</SortableTh>
                                <th>Phone</th><SortableTh sortKey="insurance" sort={tableSort}>Insurance</SortableTh><th>Devices</th><th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.patients.map((p) => (
                                <tr key={p._id}>
                                    <td>{p.lastname}, {p.firstname}</td>
                                    <td>{p.emrId}</td>
                                    <td>{formatDob(p.DOB)}</td>
                                    <td>{p.patientType}</td>
                                    <td>{p.mobileNo || p.phone1 || ''}</td>
                                    <td>{p.insurancecompany || ''}</td>
                                    <td>{(p.assigned_devices || []).length}</td>
                                    <td>
                                        <button className="btn btn-link btn-sm" type="button" onClick={() => setEditing(p)}>Edit</button>
                                        <Link className="btn btn-link btn-sm" to={{ pathname: '/devices/assign', state: { patient: p } }}>Devices</Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
                <div className="d-flex justify-content-between">
                    <small className="text-muted">{data.count || 0} patients</small>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-outline-secondary" type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>⟨</button>
                        <small className="mt-1">{page} / {pages}</small>
                        <button className="btn btn-sm btn-outline-secondary" type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>⟩</button>
                    </div>
                </div>

                <Modal size="lg" show={!!editing} onHide={() => setEditing(null)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editing && editing._id ? 'Edit patient basics' : 'Add patient'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {editing ? (
                            <PatientBasicsForm
                                patient={editing._id ? editing : null}
                                onCancel={() => setEditing(null)}
                                onSaved={() => { setEditing(null); load(); }}
                            />
                        ) : null}
                    </Modal.Body>
                </Modal>
            </RoleShell>
        </>
    );
};

export default EnrollmentPatients;
